import * as fflate from 'fflate';
import { convex, featureCollection, multiPolygon, point, polygon } from '@turf/turf';
import { Assets, CanvasSource, DOMAdapter, Matrix, PointData, Sprite } from 'pixi.js';
import { environment } from 'src/environments/environment';
import { IRoomAsset } from 'src/sync/_api';

export class Util {
    public static fflate = fflate;
    private static groupCollapsed = console.groupCollapsed;
    private static warn = console.warn;

    public static isEmpty(obj: any) {
        for (let k in obj) return false;
        return true;
    }

    public static clone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    public static arrayMove<T>(arr: T[], from: number, to: number) {
        const element = arr[from];
        arr.splice(from, 1);
        arr.splice(to, 0, element);
    }

    public static suppressWarnings<T>(f: () => T) {
        console.groupCollapsed = () => { };
        console.warn = () => { };
        const output = f();
        console.groupCollapsed = Util.groupCollapsed;
        console.warn = Util.warn;
        return output;
    }

    public static pointsToHull(points: PointData[]) {
        const turfpoints = points.map(p => point([p.x, p.y]));
        const feature = featureCollection(turfpoints);
        const hull = convex(feature);
        return hull?.geometry.coordinates.flat(1);
    }

    public static pathDifference(a: number[], b: number[]): number[] {
        if (a[0] == b[0] && a[1] == b[1]) {
            return [a[2], a[3], b[2], b[3]];

        } else if (a[0] == b[2] && a[1] == b[3]) {
            return [a[2], a[3], b[0], b[1]];

        } else if (a[2] == b[0] && a[3] == b[1]) {
            return [a[0], a[1], b[2], b[3]];

        } else if (a[2] == b[2] && a[3] == b[3]) {
            return [a[0], a[1], b[0], b[1]];
        }
        return [];
    }

    public static pathToPositions(path: number[]): number[][] {
        const chunks = []
        for (let i = 0; i < path.length; i += 2) {
            const chunk = path.slice(i, i + 2);
            chunks.push(chunk);
        }
        return chunks;
    }

    public static pathToPoints(array: number[]) {
        const points = [];
        for (let i = 0; i < array.length; i += 2) {
            points.push({ x: array[i], y: array[i + 1] });
        }
        return points;
    }

    public static pathToPolygon(array: number[]) {
        const poly = array.reduce((a: number[][], x: number, i: number) => {
            i % 2 == 0 ? a.push([x]) : a[Math.floor(i / 2)].push(x);
            return a;
        }, []);
        poly.push([array[0], array[1]]);

        return polygon([poly]);
    }

    public static pathToMultiPolygon(array: number[]) {
        const polygon = array.reduce((a: number[][], x: number, i: number) => {
            i % 2 == 0 ? a.push([x]) : a[Math.floor(i / 2)].push(x);
            return a;
        }, []);
        polygon.push([array[0], array[1]]);

        return multiPolygon([[polygon]]);
    }

    // TODO: Move this into the colorpicker itself
    public static getRandomHexcolor(): string {
        return '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0').toUpperCase();
    }

    public static pick<T>(list: T[]): T {
        if (list.length == 0) throw new Error('Pick called on an empty array!');
        return list[Math.floor(Math.random() * list.length)];
    }

    public static randBetween(min: number, max: number) {
        return Math.random() * (max - min) + min;
    }

    public static median(values: number[]): number {
        values = [...values].sort((a, b) => a - b);
        const half = Math.floor(values.length / 2);

        return (values.length % 2
            ? values[half]
            : (values[half - 1] + values[half]) / 2
        );
    }

    public static equal(lhs: Matrix, rhs: Matrix) {
        if (lhs == null || rhs == null) return false;
        return lhs.a === rhs.a
            && lhs.b === rhs.b
            && lhs.c === rhs.c
            && lhs.d === rhs.d
            && lhs.tx === rhs.tx
            && lhs.ty === rhs.ty;
    }

    public static isAffirmativeKey(e: KeyboardEvent) {
        return e.code == 'Enter' || e.code == 'NumpadEnter' || e.keyCode == 13;
    }

    public static sortByFrequency(array: number[]) {
        const frequency: any = {};

        array.forEach(function (value) { frequency[value] = 0; });

        const uniques = array.filter(function (value) {
            return ++frequency[value] === 1;
        });

        return uniques.sort(function (a, b) {
            return frequency[b] - frequency[a];
        });
    }

    public static uniqueBy<T>(array: T[], by: Function) {
        const seen: any = {};
        return array.filter(function (item) {
            const k = by(item);
            return Object.prototype.hasOwnProperty.call(seen, k) ? false : (seen[k] = true);
        });
    }

    public static hypotenuse(x: number, y: number) {
        return Math.sqrt((x * x) + (y * y));
    }

    public static distance(p1: PointData, p2: PointData) {
        const a = p2.y - p1.y;
        const b = p2.x - p1.x;
        const c = Util.hypotenuse(a, b);
        return c;
    }

    /* github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js */
    public static hash(str: string, seed: number = 0) {
        let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
        for (let i = 0, ch; i < str.length; i++) {
            ch = str.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
        h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
        h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

        return 4294967296 * (2097151 & h2) + (h1 >>> 0);
    };

    public static saveFile(filename: string, data: Uint8Array) {
        const url = window.URL.createObjectURL(new Blob([data as any]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.style.display = 'none';
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    }

    public static getRadianAngle(p1: PointData, p2: PointData) {
        const a = p2.y - p1.y;
        const b = p2.x - p1.x;
        const c = Math.sqrt((a * a) + (b * b));
        let angle = Math.asin(b / c);
        if (a > 0) angle = Math.PI - angle;
        return angle;
    }

    public static circleInscribedPath(cx: number, cy: number, radius: number, sides = 48, angle = 0) {
        const TWO_PI = 2 * Math.PI;
        const segmentLength = TWO_PI / sides;
        const array = [];
        for (let a = angle, i = 0; i < sides; a += segmentLength, i++) {
            let x = cx + Math.cos(a) * radius;
            let y = cy + Math.sin(a) * radius;
            array.push(Math.round(x), Math.round(y));
        }
        return array;
    }

    public static imageToCanvas(image: HTMLImageElement): CanvasSource {
        const canvas = DOMAdapter.get().createCanvas(image.width, image.height);
        const context = canvas.getContext('2d');
        context!.drawImage(image, 0, 0, image.width, image.height);
        const source = new CanvasSource({ resource: canvas });
        return source;
    }

    public static async loadImageAsync(asset: IRoomAsset): Promise<Sprite> {
        try {
            const texture = await Assets.load(`https://${environment.assetsUrl}/${asset.name}`);
            return Sprite.from(texture);
        } catch (error) {
            const fallbackAsset = await Assets.load('./assets/textures/question.png');
            return Sprite.from(fallbackAsset);
        }
    }

    public static get roomUrl(): string {
        // (noten) TODO: Fragile, should be re-written in the future
        const route = window.location.pathname.split('/').filter(Boolean);
        const hostuser = route[0];
        const roomName = route[1];
        return `${environment.hostUrl}/rooms/${hostuser}/${roomName}`;
    }
}
